import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent, StatCard } from '@themes/aegix';
import { HiArrowLeft } from 'react-icons/hi2';
import { useDistrictReadiness } from '../../hooks/queries/useDocuments';
import type {
  SchoolReadiness,
} from '../../services/documents.service';

interface ReadinessOverviewProps {
  onBack: () => void;
}

function statusLabel(status: string, t: (key: string, fallback: string) => string): string {
  switch (status) {
    case 'ready':
      return t('documents.ready', 'Ready');
    case 'mostly_ready':
      return t('documents.mostlyReady', 'Mostly Ready');
    case 'needs_attention':
      return t('documents.needsAttention', 'Needs Attention');
    case 'not_ready':
      return t('documents.notReady', 'Not Ready');
    default:
      return status;
  }
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  return (
    <Box>
      <Flex justify="space-between" mb={1}>
        <Text fontSize="sm" color="text.secondary">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight={600} color="text.primary">
          {score}%
        </Text>
      </Flex>
      <Box
        bg="surface.secondary"
        borderRadius="full"
        h="8px"
        overflow="hidden"
      >
        <Box
          bg={score >= 80 ? 'green.500' : score >= 50 ? 'yellow.500' : 'text.danger'}
          h="100%"
          w={`${score}%`}
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
    </Box>
  );
}

function SchoolCard({ school }: { school: SchoolReadiness }) {
  const { t } = useTranslation();
  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      p={4}
    >
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight={600} color="text.primary">
          {school.school_name}
        </Text>
        <Text
          fontSize="lg"
          fontWeight={700}
          color={
            school.overall_score >= 80
              ? 'green.500'
              : school.overall_score >= 50
                ? 'yellow.500'
                : 'text.danger'
          }
        >
          {school.overall_score}%
        </Text>
      </Flex>

      <VStack gap={2} align="stretch">
        <ScoreBar label={t('reunification.plans', 'Plans')} score={school.plan_readiness} />
        <ScoreBar label={t('nav.sites', 'Sites')} score={school.site_readiness} />
        <ScoreBar label={t('common.staff', 'Staff')} score={school.staff_readiness} />
        <ScoreBar label={t('documents.communications', 'Communications')} score={school.communication_readiness} />
        <ScoreBar label={t('documents.studentRosters', 'Student Rosters')} score={school.student_roster_completeness} />
        <ScoreBar label={t('documents.equipment', 'Equipment')} score={school.equipment_readiness} />
      </VStack>

      {school.recommendations.length > 0 && (
        <Box mt={3} pt={3} borderTop="1px solid" borderColor="border.primary">
          <Text fontSize="xs" fontWeight={600} color="text.secondary" mb={1}>
            {t('documents.recommendations', 'Recommendations')}
          </Text>
          {school.recommendations.map((rec, i) => (
            <Text key={i} fontSize="xs" color="text.secondary">
              &bull; {rec}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function ReadinessOverview({ onBack }: ReadinessOverviewProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useDistrictReadiness();

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex align="center" gap={3} mb={6}>
        <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h2" fontSize="lg" fontWeight={600} color="text.primary">
            {t('documents.readinessAssessments', 'Readiness Assessments')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.districtWidePreparedness', 'District-wide reunification preparedness')}
          </Text>
        </Box>
      </Flex>

      {isLoading && (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      )}

      {error && (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadReadiness', 'Failed to load readiness data')}
          </Text>
        </Center>
      )}

      {data && (
        <VStack gap={6} align="stretch">
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            <StatCard
              label={t('documents.overallScore', 'Overall Score')}
              value={`${data.overall_score}%`}
            />
            <StatCard
              label={t('common.status', 'Status')}
              value={statusLabel(data.overall_status, t)}
            />
            <StatCard
              label={t('documents.sitesReady', 'Sites Ready')}
              value={`${data.schools_ready} / ${data.total_schools}`}
            />
            <StatCard
              label={t('documents.needsAttention', 'Needs Attention')}
              value={String(data.schools_not_ready)}
            />
          </SimpleGrid>

          <Box>
            <Text fontWeight={600} color="text.primary" mb={3}>
              {t('documents.siteAssessments', 'Site Assessments')}
            </Text>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
              {data.school_assessments.map((school) => (
                <SchoolCard key={school.school_id} school={school} />
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      )}
    </Box>
  );
}
